import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, User } from 'lucide-react';

interface WorkerJobsProtectedRouteProps {
  children: React.ReactNode;
}

export const WorkerJobsProtectedRoute = ({ children }: WorkerJobsProtectedRouteProps) => {
  const { userProfile } = useAuth();

  // If user is not a worker, show access denied page
  if (!userProfile?.is_worker) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-amber-800">Worker Profile Required</CardTitle>
                <p className="text-sm text-amber-700">
                  You need to complete your worker profile to access job opportunities.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-amber-700">
              The Jobs section is only available for users who have completed their worker profile 
              with skills, experience, and trade information. This helps match you with relevant job opportunities.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/profile" className="flex-1">
                <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                  <User className="h-4 w-4 mr-2" />
                  Complete Worker Profile
                </Button>
              </Link>
              <Link to="/dashboard/homeowner" className="flex-1">
                <Button variant="outline" className="w-full border-amber-200 text-amber-700 hover:bg-amber-100">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default WorkerJobsProtectedRoute;